import { z } from 'zod';
import { LEAD_ACTIVITY_KINDS, LEAD_APPOINTMENT_OUTCOMES, LEAD_NOTE_KINDS, LEAD_SOURCES, LEAD_STAGES, ROLES } from '@bulk/types';
import { appointmentSchema } from './appointments';
import { dateTimeSchema, idSchema, patchOf, searchQuerySchema, sortOrderSchema } from './common';
import { createMemberSchema, memberSchema } from './members';

export const createLeadSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().max(32).nullable().optional(),
  email: z.email().toLowerCase().nullable().optional(),
  source: z.enum(LEAD_SOURCES).default('WALK_IN'),
  interest: z.string().trim().max(500).nullable().optional().describe('What they came for, in their words: a plan, personal training, a class'),
  locationId: idSchema.nullable().optional(),
  assigneeId: idSchema.nullable().optional().describe('The staff member working the lead, from `GET /leads/assignees`'),
  nextFollowUpAt: dateTimeSchema.nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type CreateLeadInput = z.infer<typeof createLeadSchema>;

export const updateLeadSchema = patchOf(createLeadSchema);
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>;

export const moveLeadSchema = z.object({
  stage: z.enum(LEAD_STAGES).describe('WON is reached only by converting the lead into a member'),
  lostReason: z.string().trim().max(500).nullable().optional().describe('Required when the stage is LOST'),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type MoveLeadInput = z.infer<typeof moveLeadSchema>;

export const leadNoteSchema = z.object({
  kind: z.enum(LEAD_NOTE_KINDS).default('NOTE'),
  text: z.string().trim().min(1).max(2000),
});
export type LeadNoteInput = z.infer<typeof leadNoteSchema>;

export const convertLeadSchema = z
  .object({
    member: createMemberSchema.optional().describe("A new member, usually the lead's details as the form prefilled them"),
    memberId: idSchema.optional().describe('Or a member the gym already has'),
    soldByUserId: idSchema.optional().describe('The staff member credited with the conversion, from `GET /targets/sellers`; the caller when left out'),
  })
  .refine((input) => (input.member === undefined) !== (input.memberId === undefined), { message: 'Send either a new member or an existing memberId', path: ['member'] });
export type ConvertLeadInput = z.infer<typeof convertLeadSchema>;

export const LEAD_LIST_SORTS = ['createdAt', 'name', 'stage', 'source', 'nextFollowUpAt', 'stageChangedAt'] as const;
export type LeadListSort = (typeof LEAD_LIST_SORTS)[number];

/** TODAY: due on the gym's calendar today. OVERDUE: due before today. DUE: either. Only open leads are ever due. */
export const LEAD_FOLLOW_UP_FILTERS = ['TODAY', 'OVERDUE', 'DUE'] as const;
export type LeadFollowUpFilter = (typeof LEAD_FOLLOW_UP_FILTERS)[number];

export const LEAD_ASSIGNEE_NONE = 'none';
export const LEAD_ASSIGNEE_ME = 'me';

const leadFiltersShape = {
  source: z.enum(LEAD_SOURCES).optional(),
  assigneeId: z.union([idSchema, z.literal(LEAD_ASSIGNEE_NONE), z.literal(LEAD_ASSIGNEE_ME)]).optional().describe("A staff id, `none` for unassigned leads or `me` for the caller's"),
  locationId: idSchema.optional(),
  followUp: z.enum(LEAD_FOLLOW_UP_FILTERS).optional(),
};

export const leadListQuerySchema = searchQuerySchema.extend({
  ...leadFiltersShape,
  stage: z.enum(LEAD_STAGES).optional(),
  open: z.stringbool().optional().describe('Only leads still being worked: not won, not lost'),
  sort: z.enum(LEAD_LIST_SORTS).default('createdAt'),
  order: sortOrderSchema.default('desc'),
});
export type LeadListQuery = z.infer<typeof leadListQuerySchema>;

export const leadBoardQuerySchema = z.object({ q: z.string().trim().max(120).optional(), ...leadFiltersShape });
export type LeadBoardQuery = z.infer<typeof leadBoardQuerySchema>;

export const leadAssigneeSchema = z.object({
  id: idSchema,
  userId: idSchema,
  name: z.string(),
  role: z.enum(ROLES),
});
export type LeadAssigneeDto = z.infer<typeof leadAssigneeSchema>;

export const leadSchema = z.object({
  id: idSchema,
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  source: z.enum(LEAD_SOURCES),
  interest: z.string().nullable(),
  locationId: idSchema.nullable(),
  locationName: z.string().nullable(),
  assignee: leadAssigneeSchema.omit({ role: true }).nullable(),
  stage: z.enum(LEAD_STAGES),
  stageChangedAt: z.string(),
  nextFollowUpAt: z.string().nullable(),
  followUpDue: z.enum(['TODAY', 'OVERDUE']).nullable().describe("Whether the follow-up falls due today or before today on the gym's calendar; null when it is later, unset, or the lead is closed"),
  notes: z.string().nullable(),
  lostReason: z.string().nullable(),
  memberId: idSchema.nullable(),
  memberName: z.string().nullable(),
  nextAppointment: z
    .object({ id: idSchema, startsAt: z.string(), typeName: z.string(), typeNameLocalized: z.record(z.string(), z.string()) })
    .nullable()
    .describe('The next tour or consultation booked for the lead that has not started; null when none is'),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LeadDto = z.infer<typeof leadSchema>;

/** Values a timeline entry carries for the browser to word: which fields changed, who it went to, which member it became. */
export const leadActivityDetailsSchema = z.object({
  fields: z.array(z.string()).optional(),
  source: z.enum(LEAD_SOURCES).optional(),
  assigneeName: z.string().nullable().optional(),
  followUpAt: z.string().nullable().optional(),
  lostReason: z.string().optional(),
  memberId: idSchema.optional(),
  memberName: z.string().optional(),
  matchedBy: z.enum(['phone', 'email']).optional(),
  appointment: z
    .object({
      id: idSchema,
      outcome: z.enum(LEAD_APPOINTMENT_OUTCOMES),
      typeName: z.string(),
      typeNameLocalized: z.record(z.string(), z.string()),
      startsAt: z.string(),
      hostName: z.string(),
    })
    .optional(),
});
export type LeadActivityDetails = z.infer<typeof leadActivityDetailsSchema>;

export const leadActivitySchema = z.object({
  id: idSchema,
  kind: z.enum(LEAD_ACTIVITY_KINDS),
  fromStage: z.enum(LEAD_STAGES).nullable(),
  toStage: z.enum(LEAD_STAGES).nullable(),
  text: z.string().nullable(),
  details: leadActivityDetailsSchema,
  actorName: z.string().nullable().describe('Who did it; null for the website and the system'),
  createdAt: z.string(),
});
export type LeadActivityDto = z.infer<typeof leadActivitySchema>;

export const leadDetailSchema = leadSchema.extend({
  activities: z.array(leadActivitySchema).describe('Newest first'),
  appointments: z.array(appointmentSchema).describe("Tours and consultations booked for the lead, newest first; empty when the gym's appointments module is off"),
});
export type LeadDetailDto = z.infer<typeof leadDetailSchema>;

export const leadBoardSchema = z.object({
  columns: z.array(
    z.object({
      stage: z.enum(LEAD_STAGES),
      total: z.number().int(),
      items: z.array(leadSchema).describe('Open stages: the soonest follow-up first, then the longest waiting. Won and lost: the most recent first'),
    }),
  ),
  limit: z.number().int(),
});
export type LeadBoardDto = z.infer<typeof leadBoardSchema>;

export const leadFollowUpsSchema = z.object({
  dueToday: z.number().int(),
  overdue: z.number().int(),
  mineDueToday: z.number().int(),
  mineOverdue: z.number().int(),
});
export type LeadFollowUpsDto = z.infer<typeof leadFollowUpsSchema>;

export const convertLeadResultSchema = z.object({ lead: leadSchema, member: memberSchema });
export type ConvertLeadResultDto = z.infer<typeof convertLeadResultSchema>;
